/**
 * ProjectStatusSelect — the dashboard project card's lifecycle status badge,
 * as a user-editable control (T284464051).
 *
 * Backend auto-advancement (schemas' `projectStatusOnComplete` ->
 * `_advance_project_status`) is monotonic and only ever moves a project
 * forward, so it can express "work has started" and "the video was exported"
 * but never "I consider this shipped". Workflows without an export terminal —
 * webtoons, in particular — have no automatic route to `completed` at all.
 * This control is that route.
 *
 * Deliberately NOT a native <select>: the OS-rendered option popup ignores our
 * theme entirely (white system menu over a near-black dashboard) and cannot
 * carry the per-status colours. This mirrors the dashboard's existing
 * `SortSelect` (DashboardTabBar.tsx) — trigger button + absolutely positioned
 * `bg-surface-1` menu, closed on outside mousedown — so the two dropdowns on
 * this page look and behave alike.
 *
 * The card body is a <Link>, so every interactive element here stops
 * propagation; otherwise opening the menu would navigate to the workspace.
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ProjectStatus } from '../../api/types'

const STATUS_OPTIONS: readonly ProjectStatus[] = ['draft', 'in_progress', 'completed']

/**
 * Per-status colour scheme.
 *
 * `primary` (#D4A043) is the same warm amber as the "+ New Project" button;
 * `success` (#22C55E) is the shared semantic green. Draft stays neutral grey so
 * an untouched project reads as inert rather than as a state worth noticing.
 */
const STATUS_STYLES: Record<
  ProjectStatus,
  { trigger: string; dot: string; activeText: string }
> = {
  draft: {
    trigger: 'text-text-secondary bg-surface-2 border-white/10 hover:border-white/25',
    dot: 'bg-text-secondary',
    activeText: 'text-text-secondary',
  },
  in_progress: {
    trigger: 'text-primary bg-primary/10 border-primary/30 hover:border-primary/50',
    dot: 'bg-primary',
    activeText: 'text-primary',
  },
  completed: {
    trigger: 'text-success bg-success/10 border-success/30 hover:border-success/50',
    dot: 'bg-success',
    activeText: 'text-success',
  },
}

/**
 * `status` arrives from the API (and ultimately from the untyped
 * `projects.data` JSONB), so it is not guaranteed to be one of the three known
 * values — a legacy or future row can carry anything. Indexing STATUS_STYLES
 * blindly would throw and take the whole dashboard card tree with it, which is
 * a badly disproportionate failure for a badge. Unknown values fall back to the
 * neutral scheme and render verbatim.
 *
 * This is deliberately NOT the "generated lookups must succeed" case in
 * CLAUDE.md invariant 2: that governs schema-derived maps where a miss is a
 * codegen bug, not values crossing a network boundary.
 */
function stylesFor(status: ProjectStatus): (typeof STATUS_STYLES)[ProjectStatus] {
  return STATUS_STYLES[status] ?? STATUS_STYLES.draft
}

/**
 * Same colour scheme for the read-only badge the drag overlay renders (it
 * cannot host a live dropdown — see HomePage). Exported so the overlay and the
 * real control never drift apart.
 */
export function projectStatusBadgeClass(status: ProjectStatus): string {
  return stylesFor(status).trigger
}

export function projectStatusDotClass(status: ProjectStatus): string {
  return stylesFor(status).dot
}

interface ProjectStatusSelectProps {
  projectId: string
  value: ProjectStatus
  onChange: (status: ProjectStatus) => void
  disabled?: boolean
}

export function ProjectStatusSelect({
  projectId,
  value,
  onChange,
  disabled = false,
}: ProjectStatusSelectProps) {
  const { t } = useTranslation('pages')
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const styles = stylesFor(value)

  // An unrecognised status has no `home.status.*` key; i18next would echo the
  // key back ("home.status.active"). Show the raw value instead.
  const labelFor = (status: ProjectStatus) =>
    status in STATUS_STYLES ? t(`home.status.${status}`) : String(status)

  useEffect(() => {
    if (!open) return
    const onMouseDown = (e: MouseEvent) => {
      if (e.target instanceof Node && ref.current && !ref.current.contains(e.target)) {
        setOpen(false)
      }
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  // Close if the row re-renders into a disabled (in-flight) state.
  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])

  const stop = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <div className="relative" ref={ref} onClick={stop}>
      <button
        type="button"
        data-testid={`project-status-${projectId}`}
        // Tests and NAD traces read the raw enum here; the visible label is
        // translated, so asserting on text would be locale-dependent.
        data-status={value}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('home.statusLabel')}
        title={t('home.statusLabel')}
        onClick={(e) => {
          stop(e)
          setOpen((prev) => !prev)
        }}
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xxs font-medium uppercase
          tracking-wider border rounded-full cursor-pointer transition-colors
          focus:outline-none focus-visible:ring-1 focus-visible:ring-ring
          disabled:cursor-wait disabled:opacity-60 ${styles.trigger}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${styles.dot}`} aria-hidden="true" />
        {labelFor(value)}
        <svg
          className={`w-2.5 h-2.5 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={t('home.statusLabel')}
          data-testid={`project-status-menu-${projectId}`}
          className="absolute top-full left-0 mt-1 bg-surface-1 border border-border rounded-lg
            shadow-xl z-50 min-w-[150px] py-1 animate-fade-in"
        >
          {STATUS_OPTIONS.map((status) => {
            const isActive = status === value
            const optionStyles = STATUS_STYLES[status]
            return (
              <button
                key={status}
                type="button"
                role="option"
                aria-selected={isActive}
                data-testid={`project-status-option-${projectId}-${status}`}
                onClick={(e) => {
                  stop(e)
                  setOpen(false)
                  if (!isActive) onChange(status)
                }}
                className={`w-full text-left px-3 py-1.5 text-xs font-medium transition-colors
                  flex items-center gap-2 hover:bg-surface-2
                  ${isActive ? optionStyles.activeText : 'text-text-secondary hover:text-foreground'}`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${optionStyles.dot}`}
                  aria-hidden="true"
                />
                <span className="flex-1">{labelFor(status)}</span>
                {isActive && (
                  <svg
                    className="w-3 h-3 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
